/**
 * SPA.6 — the whole annotation convention, three attributes (process/01-UI-CONTRACT.md §2).
 *
 * The branded types are the compile-time half; `eslint-rules/` is the build-time half that
 * actually rejects a bad value (a branded type alone does not stop `"data-scr"` from being typed
 * as a plain string literal at the call site — TypeScript brands are not checked at a string
 * literal, only when a value flows through a function that requires the brand). The three ESLint
 * rules are what LLD-SPA-143 calls "the lint rule… added before the first component exists", and
 * they read the same generated JSON this module's helpers do.
 */
import unitIds from './generated/unit-ids.json'
import actionIds from './generated/action-ids.json'
import componentIds from './generated/component-ids.json'

export type UnitId = string & { readonly __brand: 'data-scr' }
export type ComponentId = string & { readonly __brand: 'data-cmp' }
export type ActionId = string & { readonly __brand: 'data-act' }

const UNIT_ID_SET: ReadonlySet<string> = new Set(unitIds)
const ACTION_ID_SET: ReadonlySet<string> = new Set(actionIds)
const COMPONENT_ID_SET: ReadonlySet<string> = new Set(componentIds)

/** Throws rather than returning a boolean — a caller constructing one of these three attributes
 * from a dynamic value (not a literal the lint rule already checked) has no other place to find
 * out it was wrong. */
export function unitId(value: string): UnitId {
  if (!UNIT_ID_SET.has(value)) {
    throw new Error(`"${value}" is not in process/03-SCREEN-INVENTORY.md §0's unit register`)
  }
  return value as UnitId
}

export function componentId(value: string): ComponentId {
  if (!COMPONENT_ID_SET.has(value)) {
    throw new Error(`"${value}" is not in process/08-COMPONENT-CATALOGUE.md's mapped table`)
  }
  return value as ComponentId
}

export function actionId(value: string): ActionId {
  if (value.startsWith('nav.')) {
    const routeId = value.slice('nav.'.length)
    if (!UNIT_ID_SET.has(routeId)) {
      throw new Error(`"${value}" names route id "${routeId}", which is not in the unit register`)
    }
    return value as ActionId
  }
  if (!ACTION_ID_SET.has(value)) {
    throw new Error(`"${value}" is not in api/components/schemas/enums/ActionId.yaml`)
  }
  return value as ActionId
}
